import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Coordinate} from '../../models/coordinate';
import {CanvasMapInitializedVariables} from '../../models/canvas-map';

@Component({
  selector: 'awair-canvas-map',
  templateUrl: './canvas-map.component.html',
  styleUrls: ['./canvas-map.component.scss']
})

export class CanvasMapComponent implements OnInit {
  @ViewChild('canvas') canvasRef: ElementRef;
  @Output() changeImagePosition: EventEmitter<{ dx: number, dy: number }> = new EventEmitter<{ dx: number, dy: number }>();
  @Output() changeZoomRatio: EventEmitter<number> = new EventEmitter<number>();
  @Output() emitInitializedVariables: EventEmitter<CanvasMapInitializedVariables> = new EventEmitter<CanvasMapInitializedVariables>();
  dragFrom: Coordinate;
  canvasElement: HTMLCanvasElement;
  context: CanvasRenderingContext2D;
  image: HTMLImageElement;
  destinationX: number = 0;
  destinationY: number = 0;
  imageWidth: number;
  imageHeight: number;
  canvasWidth: number;
  canvasHeight: number;
  private zoomRatio: number;

  constructor(private el: ElementRef) {
  }

  @Input()
  set imgSrc(value: string) {
    if (value != null && value.length > 0) {
      this.image = new Image();
      this.image.src = value;
      this.image.onload = (() => {
        this.canvasElement = this.canvasRef.nativeElement;
        this.context = this.canvasElement.getContext('2d');

        // Set initial canvas element width and height to fit parent
        this.canvasElement.width = this.el.nativeElement.clientWidth;
        this.canvasElement.height = this.el.nativeElement.clientHeight;
        // Variables to be used on later computations
        this.canvasWidth = this.el.nativeElement.clientWidth;
        this.canvasHeight = this.el.nativeElement.clientHeight;

        const autoScaledSize = this.getImageSize(this.image.width, this.image.height, this.canvasWidth, this.canvasHeight);
        this.imageWidth = autoScaledSize.width;
        this.imageHeight = autoScaledSize.height;

        // Reset zoomLevel to 0 whenever new image comes in
        this.zoomLevel = 0;
        // Center dX and dY on init
        this.destinationX = ((this.canvasWidth / 2) - (this.imageWidth / 2)) * this.zoomRatio;
        this.destinationY = ((this.canvasHeight / 2) - (this.imageHeight / 2)) * this.zoomRatio;

        this.emitInitializedVariables.emit({
          canvasWidth: this.canvasWidth,
          canvasHeight: this.canvasHeight,
          dx: this.destinationX,
          dy: this.destinationY,
          imageWidth: this.imageWidth,
          imageHeight: this.imageHeight,
          zoomRatio: this.zoomRatio
        });

        this.drawImage(this.canvasElement, this.context, this.image, this.destinationX, this.destinationY, this.zoomRatio);
      });
    }
  }

  private _zoomLevel: number = 0;

  get zoomLevel(): number {
    if (this._zoomLevel != null) {
      return this._zoomLevel;
    }
  }

  @Input()
  set zoomLevel(value: number) {
    if (value != null) {
      this._zoomLevel = value;
      this.zoomRatio = Math.pow(2, value);
    }
  }

  ngOnInit() {
  }

  drawImage(canvasElement: HTMLCanvasElement, context: CanvasRenderingContext2D,
            imageElement: HTMLImageElement, dX: number, dY: number, zoomRatio: number) {
    if (context != null) {
      context.clearRect(0, 0, canvasElement.width, canvasElement.height);
      context.drawImage(
        imageElement, 0, 0,
        imageElement.width, imageElement.height,
        dX * zoomRatio, dY * zoomRatio,
        this.imageWidth * zoomRatio, this.imageHeight * zoomRatio);
    }
  }

  onMouseDown(event: MouseEvent): void {
    this.dragFrom = {x: event.offsetX, y: event.offsetY};
  }

  onMouseMove(event: MouseEvent): void {
    // When dragged, buttons = 1; else 0
    if (event.buttons > 0) {
      // This is to prevent dragFrom to not get the mouse coordinates when mouse button is pressed
      if (this.dragFrom == null) {
        this.dragFrom = {x: event.offsetX, y: event.offsetY};
      }

      const movedX: number = (event.offsetX - this.dragFrom.x);
      const movedY: number = (event.offsetY - this.dragFrom.y);

      // Prevent repaint when mousemove is triggered but hasn't moved
      // Occurs when panned slowly
      if (!(movedX === 0 && movedY === 0)) {
        // Keep adding moved coordinates to dx and dy
        // Dividing by zoomRatio to apply the scale when panning
        // Less canvas pixel when zoomed in; more canvas pixel when zoomed out
        this.destinationX += movedX / this.zoomRatio;
        this.destinationY += movedY / this.zoomRatio;

        // Reset dragFrom to moved point to prepare for next pixel movement
        this.dragFrom = {x: event.offsetX, y: event.offsetY};

        // Multiplying by zoomRatio to get moved pixel based on the original image
        this.changeImagePosition.emit({
          dx: this.destinationX * this.zoomRatio,
          dy: this.destinationY * this.zoomRatio
        });

        // Repaint
        this.drawImage(this.canvasElement, this.context, this.image, this.destinationX, this.destinationY, this.zoomRatio);
      }
    }
  }

  onMouseWheel(event: WheelEvent): void {
    event.preventDefault();

    const newZoomLevel: number = this.zoomLevel + event.deltaY * 0.001;
    const newZoomRatio: number = Math.pow(2, newZoomLevel);

    // If scrolling using the mouse wheel, keep the spot under the mouse fixed in the canvas
    const xRatio = event.offsetX / this.canvasElement.scrollWidth;
    const yRatio = event.offsetY / this.canvasElement.scrollHeight;

    const scale = this.zoomRatio / newZoomRatio;

    // Wheel affects the whole canvas so use canvasWidth instead of imageWidth
    const newWidth = scale * this.canvasWidth;
    const newHeight = scale * this.canvasHeight;

    this.destinationX -= (this.canvasWidth - newWidth) * xRatio;
    this.destinationY -= (this.canvasHeight - newHeight) * yRatio;

    this.drawImage(this.canvasElement, this.context, this.image, this.destinationX, this.destinationY, newZoomRatio);

    // Update old variables to the new ones
    this.zoomLevel = newZoomLevel;
    this.zoomRatio = newZoomRatio;
    this.canvasWidth = newWidth;
    this.canvasHeight = newHeight;

    // Emit changes
    this.changeZoomRatio.emit(newZoomRatio);
    this.changeImagePosition.emit({
      dx: this.destinationX * newZoomRatio,
      dy: this.destinationY * newZoomRatio
    });
  }

  private getImageSize(imageWidth: number, imageHeight: number, targetWidth: number, targetHeight: number):
    { width: number, height: number } {

    const ratio = Math.min(targetWidth / imageWidth, targetHeight / imageHeight);

    return {
      width: imageWidth * ratio,
      height: imageHeight * ratio
    };
  }
}
